<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { ArrowRight, FileStack, Plus, ShieldCheck } from "lucide-vue-next";
import AppShell from "@/components/workspace/AppShell.vue";
import CreateProjectFlow from "@/components/portfolio/CreateProjectFlow.vue";
import { useWorkspaceStore } from "@/stores/workspace";

const workspace = useWorkspaceStore();
const router = useRouter();
const error = ref("");
const sourceProjectId = ref("");
const createOpen = ref(false);
const selectedTemplateId = ref("");

watch(
  () => workspace.projects,
  (projects) => {
    if (
      projects.length &&
      !projects.some((project) => project.id === sourceProjectId.value)
    )
      sourceProjectId.value = projects[0]!.id;
  },
  { immediate: true },
);

onMounted(() => {
  void workspace.loadTemplates().catch(() => {
    error.value =
      "Templates could not be loaded. Check your connection and try again.";
  });
});

function openTemplateCreator() {
  if (!sourceProjectId.value) return;
  router.push({
    path: `/projects/${sourceProjectId.value}/settings`,
    query: { settings: "exports", focus: "template" },
  });
}

function createFromTemplate(templateId: string) {
  selectedTemplateId.value = templateId;
  createOpen.value = true;
}
</script>

<template>
  <AppShell>
    <main class="templates-page">
      <header>
        <div>
          <p class="eyebrow">Planning standards</p>
          <h1>Templates</h1>
          <p>
            Reuse reviewed project structure without copying owners, progress,
            comments, files, or delivery history.
          </p>
        </div>
        <div class="template-source">
          <label for="template-source-project">Create from project</label>
          <div>
            <select
              id="template-source-project"
              v-model="sourceProjectId"
              class="select"
            >
              <option
                v-for="project in workspace.projects"
                :key="project.id"
                :value="project.id"
              >
                {{ project.name }}
              </option>
            </select>
            <button
              class="button button--primary"
              type="button"
              :disabled="!sourceProjectId"
              @click="openTemplateCreator"
            >
              Open template creator <ArrowRight :size="16" />
            </button>
          </div>
        </div>
      </header>
      <p v-if="error" class="page-alert" role="alert">{{ error }}</p>
      <section class="template-sheet">
        <div class="template-table-head" aria-hidden="true">
          <span>Template</span><span>Version</span><span>Created</span
          ><span>Contents</span><span>Safety</span><span></span>
        </div>
        <article
          v-for="template in workspace.templates"
          :key="template.id"
          class="template-row"
        >
          <span class="template-name">
            <span><FileStack :size="18" /></span>
            <span
              ><strong>{{ template.name }}</strong
              ><small>{{
                template.description || "Reusable project structure"
              }}</small></span
            >
          </span>
          <span>Version {{ template.version }}</span>
          <time>{{
            new Date(template.createdAt).toLocaleDateString("en-CA", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })
          }}</time>
          <span>{{
            template.sectionCount !== undefined &&
            template.taskCount !== undefined
              ? `${template.sectionCount} sections · ${template.taskCount} tasks`
              : "Reusable structure"
          }}</span>
          <span class="safe-label"
            ><ShieldCheck :size="15" />Structure only</span
          >
          <button
            class="button button--secondary template-create"
            type="button"
            @click="createFromTemplate(template.id)"
          >
            <Plus :size="15" /> Create project
          </button>
        </article>
        <div
          v-if="!workspace.templates.length && !error"
          class="template-empty"
        >
          <FileStack :size="30" />
          <strong>No templates yet</strong>
          <p>
            Open a source project, then create a template from Project settings.
          </p>
          <button
            class="button button--primary"
            type="button"
            @click="router.push('/projects')"
          >
            Choose a project
          </button>
        </div>
      </section>
      <CreateProjectFlow
        :open="createOpen"
        :initial-template-id="selectedTemplateId"
        @close="createOpen = false"
      />
    </main>
  </AppShell>
</template>

<style scoped>
.templates-page {
  min-height: 100%;
  padding: 30px clamp(18px, 4vw, 54px) 60px;
}
.templates-page > header {
  max-width: 1180px;
  display: flex;
  justify-content: space-between;
  align-items: end;
  gap: 24px;
  margin: 0 auto 22px;
}
.templates-page h1 {
  margin: 2px 0 6px;
  color: var(--ink-900);
  font-size: 30px;
}
.templates-page header p:not(.eyebrow) {
  max-width: 680px;
  color: var(--text-soft);
}
.template-source {
  min-width: min(430px, 100%);
  display: grid;
  gap: 6px;
}
.template-source > label {
  color: var(--text-soft);
  font-size: 12px;
  font-weight: 700;
}
.template-source > div {
  display: flex;
  gap: 8px;
}
.template-source .select {
  min-width: 180px;
}
.eyebrow {
  color: var(--brand-strong);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.page-alert {
  max-width: 1180px;
  margin: 0 auto 14px;
  padding: 11px 13px;
  border: 1px solid var(--danger);
  border-radius: 5px;
  background: var(--danger-soft);
  color: var(--danger);
}
.template-sheet {
  max-width: 1180px;
  margin: auto;
  overflow: hidden;
  border: 1px solid var(--rule);
  border-radius: 8px;
  background: white;
}
.template-table-head,
.template-row {
  display: grid;
  grid-template-columns: minmax(240px, 2fr) 82px 130px 165px 125px auto;
  gap: 18px;
  align-items: center;
}
.template-table-head {
  padding: 10px 16px;
  background: var(--sheet-subtle);
  color: var(--text-soft);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
}
.template-row {
  min-height: 72px;
  padding: 11px 16px;
  border-top: 1px solid var(--rule);
}
.template-name {
  display: flex;
  align-items: center;
  gap: 10px;
}
.template-name > span:first-child {
  width: 36px;
  height: 36px;
  display: grid;
  flex: 0 0 auto;
  place-items: center;
  border-radius: 5px;
  background: var(--brand-soft);
  color: var(--brand-strong);
}
.template-name > span:last-child {
  min-width: 0;
  display: grid;
  gap: 3px;
}
.template-name small {
  overflow: hidden;
  color: var(--text-soft);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.safe-label {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: var(--success);
  font-size: 12px;
  font-weight: 700;
}
.template-create {
  white-space: nowrap;
}
.template-empty {
  min-height: 280px;
  display: grid;
  place-items: center;
  align-content: center;
  gap: 8px;
  color: var(--text-soft);
  text-align: center;
}
@media (max-width: 760px) {
  .templates-page > header {
    align-items: stretch;
    flex-direction: column;
  }
  .template-source > div {
    align-items: stretch;
    flex-direction: column;
  }
  .template-table-head {
    display: none;
  }
  .template-row {
    grid-template-columns: 1fr 1fr;
  }
  .template-name {
    grid-column: 1 / -1;
  }
}
</style>
